import { useState } from "react";
import { money, num } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CheckCircle2, AlertTriangle, Info, Zap } from "lucide-react";
import { resolveCarrierBrand } from "@/lib/constants";

function CarrierAvatar({ rate }) {
  const { logo, color, initials } = resolveCarrierBrand(rate);
  const [failed, setFailed] = useState(false);
  if (logo && !failed) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-white" data-testid={`carrier-logo-${rate.carrier_code}`}>
        <img
          src={logo}
          alt={rate.carrier_name}
          className="h-5 w-5 object-contain"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  return (
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[10px] font-bold tracking-tight text-white"
      style={{ background: color }}
      data-testid={`carrier-logo-${rate.carrier_code}`}
    >
      {initials}
    </span>
  );
}

function Confidence({ score, level }) {
  const color = score >= 70 ? "var(--success)" : score >= 50 ? "var(--warning)" : "var(--destructive)";
  return (
    <div className="flex items-center justify-end gap-1.5">
      <div className="h-1.5 w-12 overflow-hidden rounded-full bg-secondary">
        <div className="h-full rounded-full" style={{ width: `${score}%`, background: `hsl(${color})` }} />
      </div>
      <span className="metric-num text-xs" style={{ color: `hsl(${color})` }}>{score}</span>
    </div>
  );
}

function SourceBadge({ rate }) {
  if (rate.broker === "PRIORITY1") {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#F47C20]" data-testid={`rate-source-${rate.carrier_code}`}>
        via Priority1 · +{rate.markup_pct ?? 10}%
      </span>
    );
  }
  if (rate.broker === "GLOBALTRANZ") {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#0072CE]" data-testid={`rate-source-${rate.carrier_code}`}>
        via GlobalTranz
      </span>
    );
  }
  const map = {
    live: ["text-[hsl(var(--success))]", "Direct API"],
    mock: ["text-[hsl(var(--warning))]", "PHS Estimate"],
    manual: ["text-zinc-400", "Manual"],
    cache: ["text-[hsl(var(--primary))]", "Cached"],
  };
  const [cls, fallbackLabel] = map[rate.rate_source] || map.mock;
  const label = rate.rate_source === "live" ? "Direct API" : (rate.rate_label || fallbackLabel);
  return <span className={`text-[10px] font-bold uppercase tracking-wider ${cls}`} data-testid={`rate-source-${rate.carrier_code}`}>{label}</span>;
}

export default function CarrierComparison({ result, selectedCode, onSelect, manualRate, onManualRate, variant = "classic" }) {
  const rates = result?.rates || [];

  if (!result) {
    return (
      <div className="flex h-32 items-center justify-center border border-dashed border-border bg-card text-sm text-muted-foreground">
        Carrier rates appear here after calculation.
      </div>
    );
  }

  const lowest = result.recommended_carrier_code;
  const payable = rates.filter((r) => r.total_carrier_cost > 0).sort((a, b) => a.total_carrier_cost - b.total_carrier_cost);
  const HIDDEN_CARRIERS = ["tax air", "performance freight", "central transport"];
  const displayRates = [...rates]
    .filter((r) => !HIDDEN_CARRIERS.some((k) => (r.carrier_name || "").toLowerCase().includes(k)))
    .sort(
      (a, b) => (Number(a.total_carrier_cost <= 0) - Number(b.total_carrier_cost <= 0)) || (a.total_carrier_cost - b.total_carrier_cost),
    );
  const lowestTotal = payable[0]?.total_carrier_cost ?? 0;
  const nextBest = payable.find((r) => r.total_carrier_cost > lowestTotal);
  const savings = nextBest ? +(nextBest.total_carrier_cost - lowestTotal).toFixed(2) : 0;
  const savingsPct = nextBest && nextBest.total_carrier_cost > 0 ? Math.round((savings / nextBest.total_carrier_cost) * 100) : 0;

  return (
    <div className="border border-border bg-card" data-testid="carrier-comparison">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="font-heading text-base font-bold tracking-tight">Carrier Rate Comparison</h3>
        <span className="label-caps">{rates.length} providers</span>
      </div>
      <TooltipProvider>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="w-[180px]">Carrier</TableHead>
                <TableHead>Service</TableHead>
                <TableHead className="text-right">Transit</TableHead>
                <TableHead className="text-right">Linehaul</TableHead>
                <TableHead className="text-right">Fuel</TableHead>
                <TableHead className="text-right">Access.</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Confidence</TableHead>
                <TableHead className="text-center">Select</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRates.map((r) => {
                const isLowest = r.carrier_code === lowest;
                const isSelected = r.carrier_code === selectedCode;
                const hasIssue = (r.errors || []).length > 0;
                return (
                  <TableRow
                    key={r.carrier_code}
                    data-testid={`carrier-row-${r.carrier_code}`}
                    className={`border-border cursor-pointer ${isSelected ? "bg-[hsl(var(--primary))]/8" : isLowest ? "bg-[hsl(var(--success))]/8 hover:bg-[hsl(var(--success))]/12" : "hover:bg-secondary/50"}`}
                    onClick={() => onSelect(r.carrier_code)}
                    style={isLowest ? { boxShadow: "inset 3px 0 0 hsl(var(--success))" } : {}}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <CarrierAvatar rate={r} />
                        <div>
                          <div className="flex items-center gap-1.5 text-sm font-semibold leading-tight">
                            {r.carrier_name}
                            {r.broker === "PRIORITY1" && (
                              <span className="rounded-sm bg-[#F47C20]/15 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-[#F47C20]">P1</span>
                            )}
                            {r.broker === "GLOBALTRANZ" && (
                              <span className="rounded-sm bg-[#0072CE]/15 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-[#0072CE]">GTZ</span>
                            )}
                          </div>
                          <SourceBadge rate={r} />
                        </div>
                        {isLowest && (
                          <span className="flex items-center gap-1 rounded-full bg-[hsl(var(--success))] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow-sm" data-testid="best-price-badge">
                            <Zap className="h-3 w-3" /> Best Price
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.service_type || "—"}</TableCell>
                    <TableCell className="metric-num text-right text-sm">{r.transit_days ? `${r.transit_days}d` : "—"}</TableCell>
                    <TableCell className="metric-num text-right text-sm">{money(r.linehaul)}</TableCell>
                    <TableCell className="metric-num text-right text-sm text-muted-foreground">{money(r.fuel_surcharge)}</TableCell>
                    <TableCell className="metric-num text-right text-sm text-muted-foreground">
                      {(r.accessorial_detail && r.accessorial_detail.length > 0) ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help underline decoration-dotted underline-offset-2" data-testid={`accessorial-total-${r.carrier_code}`}>{money(r.accessorial_charges)}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <div className="space-y-0.5">
                              {r.accessorial_detail.map((a, i) => (
                                <div key={i} className="flex justify-between gap-3 text-xs"><span>{a.name}</span><span>{money(a.amount)}</span></div>
                              ))}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ) : money(r.accessorial_charges)}
                    </TableCell>
                    <TableCell className={`metric-num text-right text-sm font-bold ${isLowest ? "text-[hsl(var(--success))]" : ""}`}>
                      {r.carrier_code === "MANUAL" ? (
                        <Input
                          type="number"
                          value={manualRate}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => onManualRate(e.target.value)}
                          className="h-8 w-24 text-right"
                          data-testid="manual-rate-input"
                          placeholder="0.00"
                        />
                      ) : (
                        <>
                          {money(r.total_carrier_cost)}
                          {isLowest && savings > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className="mt-0.5 flex items-center justify-end gap-1 text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--success))]"
                                  data-testid="savings-badge"
                                >
                                  <Zap className="h-3 w-3" />
                                  Save {money(savings)} ({savingsPct}%)
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                Cheapest option — {money(savings)} ({savingsPct}%) below the next-best carrier
                                {nextBest ? ` (${nextBest.carrier_name} at ${money(nextBest.total_carrier_cost)})` : ""}.
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell><Confidence score={r.confidence_score} level={r.confidence_level} /></TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {hasIssue ? (
                          <Tooltip>
                            <TooltipTrigger><AlertTriangle className="h-4 w-4 text-[hsl(var(--destructive))]" /></TooltipTrigger>
                            <TooltipContent>{r.errors.join("; ")}</TooltipContent>
                          </Tooltip>
                        ) : (r.warnings || []).length ? (
                          <Tooltip>
                            <TooltipTrigger><Info className="h-4 w-4 text-[hsl(var(--warning))]" /></TooltipTrigger>
                            <TooltipContent className="max-w-xs">{r.warnings.join(" ")}</TooltipContent>
                          </Tooltip>
                        ) : null}
                        <span
                          className={`flex h-5 w-5 items-center justify-center rounded-full border ${isSelected ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]" : "border-border"}`}
                        >
                          {isSelected && <CheckCircle2 className="h-4 w-4 text-white" />}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </TooltipProvider>
      <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
        Green bar = lowest cost. Quote IDs: {rates.map((r) => `${r.carrier_code}:${r.quote_id || "—"}`).join("  ·  ")}
      </div>
    </div>
  );
}
